import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createLocalSession, openLocalSocket } from '../api';
import { LocalAudio } from '../localAudio';

interface Turn {
  role: 'user' | 'ai';
  text: string;
  isFinal: boolean;
  ts: number;
}

type Status =
  | 'idle'
  | 'starting'
  | 'connecting'
  | 'live'
  | 'closed'
  | 'error';

export function Mobile() {
  const [prompt, setPrompt] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [statusNote, setStatusNote] = useState<string>('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [steer, setSteer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [listening, setListening] = useState(false);
  const [ending, setEnding] = useState(false);
  const [hangUpNow, setHangUpNow] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const audioRef = useRef<LocalAudio | null>(null);
  const listeningRef = useRef(false);
  const turnsRef = useRef<Turn[]>([]);
  const transcriptEnd = useRef<HTMLDivElement>(null);

  useEffect(() => {
    transcriptEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns]);

  useEffect(() => {
    return () => teardown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pushTurns(next: Turn[]) {
    turnsRef.current = next;
    setTurns([...next]);
  }

  function onTranscript(role: 'user' | 'ai', text: string, isFinal: boolean, ts: number) {
    const arr = [...turnsRef.current];
    const last = arr[arr.length - 1];
    if (last && last.role === role && !last.isFinal) {
      arr[arr.length - 1] = { ...last, text, isFinal, ts };
    } else {
      arr.push({ role, text, isFinal, ts });
    }
    pushTurns(arr);
  }


  async function start(e: FormEvent) {
    e.preventDefault();
    if (!prompt.trim()) return;
    setError(null);
    setEnding(false);
    setHangUpNow(false);
    setStatus('starting');
    try {
      const task = await createLocalSession(prompt.trim());
      setStatus('connecting');

      const audio = new LocalAudio();
      audioRef.current = audio;

      const ws = openLocalSocket(task.id);
      wsRef.current = ws;

      ws.onopen = async () => {
        try {
          await audio.start((b64) => {
            // Only stream mic while actively listening (skips ringing/IVR).
            if (listeningRef.current && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'audio', audio: b64 }));
            }
          });
        } catch (err) {
          setError(
            err instanceof Error
              ? `Microphone error: ${err.message}`
              : 'Microphone access failed',
          );
          setStatus('error');
          teardown();
        }
      };

      ws.onmessage = (m) => {
        let msg: {
          type?: string;
          audio?: string;
          role?: 'user' | 'ai';
          text?: string;
          isFinal?: boolean;
          ts?: number;
          kind?: string;
          reason?: string;
          message?: string;
        };
        try {
          msg = JSON.parse(m.data);
        } catch {
          return;
        }
        if (msg.type === 'audio' && msg.audio) {
          audioRef.current?.playPcm24Base64(msg.audio);
        } else if (msg.type === 'transcript' && msg.role && msg.text !== undefined) {
          onTranscript(msg.role, msg.text, !!msg.isFinal, msg.ts ?? Date.now());
        } else if (msg.type === 'status') {
          if (msg.kind === 'standby') {
            setStatus('live');
            setListening(false);
            listeningRef.current = false;
            setStatusNote('Standby — dial now, press Start when they answer');
          } else if (msg.kind === 'listening') {
            setStatus('live');
            setListening(true);
            listeningRef.current = true;
            setStatusNote('Listening — agent is live');
          } else if (msg.kind === 'paused') {
            setListening(false);
            listeningRef.current = false;
            setStatusNote('Paused — agent not listening');
          } else if (msg.kind === 'interrupted') {
            audioRef.current?.flushPlayback();
            setStatusNote('Caller speaking…');
          } else if (msg.kind === 'agent_completed') {
            // Agent judged the call genuinely over — operator can't follow the
            // language, so make this impossible to miss.
            setEnding(true);
            setHangUpNow(true);
            setStatusNote('Agent finished the call');
          } else if (msg.kind === 'agent_wanted_to_end') {
            setStatusNote('Agent tried to wrap up — steer it or press Stop');
          } else if (msg.kind === 'wrapping_up') {
            setEnding(true);
            setStatusNote('Agent signing off…');
          } else if (msg.kind === 'closed') {
            setStatus('closed');
            setStatusNote(`Ended (${msg.reason ?? 'done'})`);
            teardown();
          } else if (msg.kind === 'error') {
            setError(msg.message ?? 'Session error');
            setStatus('error');
          }
        }
      };

      ws.onclose = () => {
        if (status === 'live' || status === 'connecting') setStatus('closed');
      };
      ws.onerror = () => setError('Connection error');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start');
      setStatus('error');
    }
  }

  function sendSteer(e: FormEvent) {
    e.preventDefault();
    const t = steer.trim();
    if (!t || wsRef.current?.readyState !== WebSocket.OPEN) return;
    // Cut the agent off immediately so the steered reply doesn't overlap the
    // sentence it was already saying (two voices).
    audioRef.current?.flushPlayback();
    wsRef.current.send(JSON.stringify({ type: 'steer', text: t }));
    setSteer('');
  }

  function resumeListening() {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    listeningRef.current = true;
    setListening(true);
    wsRef.current.send(JSON.stringify({ type: 'resume' }));
  }

  function pauseListening() {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    listeningRef.current = false;
    setListening(false);
    audioRef.current?.flushPlayback(); // silence the agent immediately
    wsRef.current.send(JSON.stringify({ type: 'pause' }));
  }

  function stop() {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // Don't tear down yet — keep the WS + speaker alive so the agent's
      // closing line plays. Server sends {kind:'closed'} when truly done.
      wsRef.current.send(JSON.stringify({ type: 'stop' }));
      listeningRef.current = false;
      setListening(false);
      setEnding(true);
      setStatusNote('Agent signing off…');
      // Safety net if the closing turn never completes.
      window.setTimeout(() => {
        teardown();
        setStatus('closed');
        setStatusNote('Stopped');
      }, 9000);
    } else {
      teardown();
      setStatus('closed');
      setStatusNote('Stopped');
    }
  }

  function teardown() {
    try {
      audioRef.current?.stop();
    } catch {
      /* ignore */
    }
    try {
      wsRef.current?.close();
    } catch {
      /* ignore */
    }
    audioRef.current = null;
    wsRef.current = null;
  }

  const running = status === 'connecting' || status === 'live';

  return (
    <div className="main-inner">
      <header className="page-header">
        <div className="eyebrow">Local bridge</div>
        <h1 className="page-title">
          Phone on <em>speaker.</em>
        </h1>
        <p className="page-subtitle">
          No carrier. You dial from your phone and put it on speaker — this PC's
          mic hears the call and its speaker talks back. Steer the agent live.
        </p>
      </header>

      {!running && status !== 'closed' && (
        <form className="form" onSubmit={start}>
          <div className="field">
            <label className="field-label" htmlFor="p">
              <span className="field-label-num">1</span> Agent prompt
            </label>
            <textarea
              id="p"
              className="textarea"
              placeholder="You are calling Dr. Lee's office. Ask the earliest available appointment next week for a checkup and confirm the address."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              autoFocus
            />
          </div>
          {error && <div className="form-error">{error}</div>}
          <div className="submit-row">
            <div className="submit-hint">
              Put your phone on speaker near this computer before starting.
            </div>
            <button
              className="btn-primary"
              type="submit"
              disabled={status === 'starting' || prompt.trim().length < 4}
            >
              {status === 'starting' ? <span className="spinner" /> : null}
              {status === 'starting' ? 'Starting' : 'Start bridge →'}
            </button>
          </div>
        </form>
      )}

      {hangUpNow && (
        <div className="hangup-banner">
          <span className="hangup-banner-icon">✆</span>
          <div>
            <div className="hangup-banner-title">Call finished — hang up your phone now</div>
            <div className="hangup-banner-sub">
              The agent has ended the conversation and signed off.
            </div>
          </div>
        </div>
      )}

      {(running || status === 'closed') && (
        <>
          <div className="live-bar">
            <span
              className="live-dot"
              style={{
                background:
                  status !== 'live'
                    ? 'var(--muted)'
                    : listening
                      ? 'var(--accent)'
                      : 'var(--signal)',
              }}
            />
            <span className="live-label">
              {status === 'connecting'
                ? 'Connecting…'
                : status !== 'live'
                  ? 'Ended'
                  : ending
                    ? 'Wrapping up…'
                    : listening
                      ? 'Listening'
                      : 'Standby'}
            </span>
            <span className="live-elapsed">{statusNote}</span>
            {running && status === 'live' && !ending && (
              <>
                {listening ? (
                  <button
                    className="btn-ghost"
                    style={{ marginLeft: 16 }}
                    onClick={pauseListening}
                  >
                    ⏸ Pause listening
                  </button>
                ) : (
                  <button
                    className="btn-primary"
                    style={{ marginLeft: 16, padding: '8px 16px' }}
                    onClick={resumeListening}
                  >
                    ▶ Start listening
                  </button>
                )}
                <button
                  className="hangup-btn"
                  style={{ marginLeft: 10 }}
                  onClick={stop}
                >
                  ■ Stop
                </button>
              </>
            )}
          </div>

          {running && !ending && (
            <form className="steer-row" onSubmit={sendSteer}>
              <input
                className="input input-mono"
                placeholder="Steer the agent live — e.g. 'ask about parking', 'wrap up now'"
                value={steer}
                onChange={(e) => setSteer(e.target.value)}
              />
              <button className="btn-ghost" type="submit" disabled={!steer.trim()}>
                Send
              </button>
            </form>
          )}

          <section className="detail-section" style={{ marginTop: 28 }}>
            <h2 className="detail-section-title">Live transcript</h2>
            <div className="transcript">
              {turns.length === 0 ? (
                <div className="transcript-empty">Waiting for audio…</div>
              ) : (
                turns.map((t, i) => (
                  <div className="transcript-turn" key={`${t.ts}-${i}`}>
                    <div className={`turn-role turn-role-${t.role === 'ai' ? 'ai' : 'callee'}`}>
                      {t.role === 'ai' ? 'Agent' : 'Caller'}
                    </div>
                    <div>
                      <div className={`turn-text${t.isFinal ? '' : ' partial'}`}>
                        {t.text}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={transcriptEnd} />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
